import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Copy, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { clienteMobileService } from '@/services/clienteMobileService';
import type { MinhaIndicacao } from '@/types/clienteMobile';
import { formatDateTime } from '@/lib/utils';

const statusLabel: Record<string, string> = {
  pendente: 'Pendente',
  em_contato: 'Em contato',
  convertido: 'Convertido',
  descartado: 'Descartado',
};

export function ClienteIndicacaoPage() {
  const [linkData, setLinkData] = useState<{ url: string; message?: string } | null>(null);
  const [indicacoes, setIndicacoes] = useState<MinhaIndicacao[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [link, list] = await Promise.all([
        clienteMobileService.getIndicacaoLink(),
        clienteMobileService.getMinhasIndicacoes(),
      ]);
      setLinkData(link);
      setIndicacoes(list);
    } catch {
      toast.error('Erro ao carregar indicações.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async () => {
    if (!linkData?.url) {
      toast.error('Link de indicação não configurado no sistema.');
      return;
    }
    try {
      await navigator.clipboard.writeText(linkData.url);
      toast.success('Link copiado!');
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Seu link de indicação</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <>
              {linkData?.message ? <p className="text-sm text-muted-foreground">{linkData.message}</p> : null}
              {linkData?.url ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <code className="flex-1 text-xs break-all rounded-lg bg-muted px-3 py-2 border border-border">
                    {linkData.url}
                  </code>
                  <Button type="button" variant="outline" onClick={copy} className="shrink-0 gap-2">
                    <Copy className="h-4 w-4" />
                    Copiar
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  O administrador precisa configurar a URL base de indicação (APP_INDICACAO_BASE_URL).
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Seus indicados</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : indicacoes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma indicação registrada ainda.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 pr-4">Nome</th>
                    <th className="py-2 pr-4">Telefone</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2">Quando</th>
                  </tr>
                </thead>
                <tbody>
                  {indicacoes.map((i) => (
                    <tr key={i.id} className="border-b border-border/60">
                      <td className="py-2 pr-4 font-medium">{i.nomeIndicado}</td>
                      <td className="py-2 pr-4">{i.telefoneIndicado}</td>
                      <td className="py-2 pr-4">{statusLabel[i.status] ?? i.status}</td>
                      <td className="py-2 text-muted-foreground whitespace-nowrap">{formatDateTime(i.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
